'use client';

import Link from 'next/link';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Divider from '@mui/material/Divider';
import ArrowForwardIcon from '@mui/icons-material/ArrowForwardOutlined';
import Editor from '../../components/Editor';
import RelatedPages from '../../components/RelatedPages';

const Code = ({ children }: { children: string }) => (
    <Box
        component="pre"
        sx={{
            backgroundColor: 'var(--surface)',
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius-lg)',
            p: 2,
            mb: 3,
            overflow: 'auto',
            fontFamily: 'monospace',
            fontSize: '0.85rem',
            lineHeight: 1.6,
        }}
    >
        {children}
    </Box>
);

const SectionTitle = ({ children }: { children: React.ReactNode }) => (
    <Typography
        variant="h2"
        sx={{
            fontSize: { xs: '1.375rem', md: '1.625rem' },
            fontWeight: 700,
            letterSpacing: '-0.01em',
            color: 'var(--fg)',
            textAlign: 'center',
            mb: 3,
        }}
    >
        {children}
    </Typography>
);

const SubTitle = ({ children }: { children: React.ReactNode }) => (
    <Typography variant="h3" gutterBottom sx={{ fontSize: '1.1rem', fontWeight: 600, color: 'var(--fg)' }}>
        {children}
    </Typography>
);

const diffHero = (
    <>
        <Typography variant="h1" gutterBottom className="hero-title">
            JSON Diff Online
        </Typography>

        <Typography variant="h2" className="hero-subtitle">
            Compare two JSON documents and see exactly what changed - every added, removed and edited value with its exact path. Free, fast, and private: everything runs in your browser.
        </Typography>

        <Typography variant="body2" sx={{ mb: 3, textAlign: 'center', color: 'var(--fg-muted)' }}>
            <Box component="strong" sx={{ color: 'var(--fg)' }}>Your JSON stays in your browser.</Box>{' '}
            Nothing is uploaded or stored by JSON Patch Online.
        </Typography>
    </>
);

export default function JsonDiffPage() {
    return (
        <Editor hero={diffHero} activePage="diff">
            <Divider sx={{ mt: 10, mb: 0, borderColor: 'var(--border)' }} />

            <Box sx={{ mt: 8 }}>
                <SectionTitle>How to compare two JSON documents</SectionTitle>
                <Grid container spacing={3}>
                    <Grid item xs={12} md={4}>
                        <Paper elevation={0} sx={{ p: 3, height: '100%', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)' }}>
                            <SubTitle>1. Paste both documents</SubTitle>
                            <Typography sx={{ color: 'var(--fg-muted)' }}>
                                Put the original JSON in the left editor and the changed version in the right one. You can also drop a .json file directly onto either editor.
                            </Typography>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <Paper elevation={0} sx={{ p: 3, height: '100%', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)' }}>
                            <SubTitle>2. Generate the diff</SubTitle>
                            <Typography sx={{ color: 'var(--fg-muted)' }}>
                                Press Generate. Both documents are validated and pretty-printed first, so formatting and whitespace never show up as false changes.
                            </Typography>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <Paper elevation={0} sx={{ p: 3, height: '100%', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)' }}>
                            <SubTitle>3. Read every change</SubTitle>
                            <Typography sx={{ color: 'var(--fg-muted)' }}>
                                Each operation in the result is one change: the exact path, what happened to it, and the new value. Copy it or download it with one click.
                            </Typography>
                        </Paper>
                    </Grid>
                </Grid>
            </Box>

            <Box sx={{ mt: 8 }}>
                <SectionTitle>An example diff, line by line</SectionTitle>
                <Box>
                    <Grid container spacing={3}>
                        <Grid item xs={12} md={6}>
                            <SubTitle>Before</SubTitle>
                            <Code>{`{
  "name": "demo",
  "version": 1,
  "tags": ["a", "b"]
}`}</Code>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <SubTitle>After</SubTitle>
                            <Code>{`{
  "version": 2,
  "tags": ["a", "b", "c"],
  "debug": true
}`}</Code>
                        </Grid>
                    </Grid>
                    <SubTitle>The diff</SubTitle>
                    <Code>{`[
  { "op": "remove", "path": "/name" },
  { "op": "replace", "path": "/version", "value": 2 },
  { "op": "add", "path": "/tags/-", "value": "c" },
  { "op": "add", "path": "/debug", "value": true }
]`}</Code>
                    <Typography sx={{ color: 'var(--fg-muted)', mb: 2 }}>
                        Read top to bottom: <code>/name</code> was removed, <code>/version</code> changed from 1 to 2, <code>"c"</code> was appended to <code>/tags</code>, and <code>/debug</code> appeared. Every change carries its JSON Pointer path, so nothing is ambiguous.
                    </Typography>
                    <Typography sx={{ color: 'var(--fg-muted)' }}>
                        The operations follow RFC 6902: see how{' '}
                        <Link href="/operations/add" style={{ color: 'var(--accent)' }}>add</Link>,{' '}
                        <Link href="/operations/remove" style={{ color: 'var(--accent)' }}>remove</Link> and{' '}
                        <Link href="/operations/replace" style={{ color: 'var(--accent)' }}>replace</Link> work, or browse{' '}
                        <Link href="/examples" style={{ color: 'var(--accent)' }}>more real-world examples</Link>.
                    </Typography>
                </Box>
            </Box>

            <Paper elevation={0} sx={{ mt: 5, p: 3, border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)' }}>
                <SubTitle>Tested output</SubTitle>
                <Typography sx={{ color: 'var(--fg-muted)', lineHeight: 1.7 }}>
                    Verified 19 September 2026 with fast-json-patch 3.1.1. For the before/after fixture above, the observed operations were <code>add /tags/2 = &quot;c&quot;</code> and <code>replace /version = 2</code>. Applying that patch reproduced the shown After document.
                </Typography>
            </Paper>

            <Box sx={{ mt: 8 }}>
                <SectionTitle>When you need a JSON diff</SectionTitle>
                <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                        <SubTitle>Code and config review</SubTitle>
                        <Typography sx={{ color: 'var(--fg-muted)', mb: 3 }}>
                            Comparing configuration files, API responses, package-lock.json changes, or infrastructure-as-code output is a daily task. A precise list of changed paths beats scanning raw files side by side.
                        </Typography>

                        <SubTitle>Debugging API responses</SubTitle>
                        <Typography sx={{ color: 'var(--fg-muted)' }}>
                            When an API starts behaving differently, diff the response you expected against the one you received. Field-level changes stand out immediately, including subtle type changes like a string becoming a number.
                        </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <SubTitle>JSON diff vs JSON Patch</SubTitle>
                        <Typography sx={{ color: 'var(--fg-muted)', mb: 3 }}>
                            A JSON diff answers "what changed" for a human. A JSON Patch (RFC 6902) is the same answer in a standard, machine-readable form. This tool gives you both at once: the operation list is the diff, readable as a change log and directly usable in an HTTP PATCH request or with any RFC 6902 library.
                        </Typography>

                        <SubTitle>Private by design</SubTitle>
                        <Typography sx={{ color: 'var(--fg-muted)' }}>
                            The comparison itself runs in your browser and JSON Patch Online does not upload the editor contents. The page still makes ordinary hosting and cookieless Vercel Analytics requests. Share links encode editor state in the URL, so do not create or share one for secrets-bearing data.
                        </Typography>
                    </Grid>
                </Grid>
            </Box>

            <Box sx={{ mt: 8 }}>
                <SectionTitle>Frequently asked questions</SectionTitle>
                <Box sx={{ maxWidth: '800px', mx: 'auto' }}>
                    <SubTitle>How do I compare two JSON files online?</SubTitle>
                    <Typography sx={{ color: 'var(--fg-muted)', mb: 3 }}>
                        Paste the original JSON into the left editor and the changed JSON into the right editor, then press Generate. The tool validates and pretty-prints both documents, then lists every difference as an operation with its exact path.
                    </Typography>

                    <SubTitle>Is this JSON diff tool free and private?</SubTitle>
                    <Typography sx={{ color: 'var(--fg-muted)', mb: 3 }}>
                        Yes. It is completely free and runs 100% client-side. The diff engine does not upload or store your editor contents. The page still makes hosting and cookieless analytics requests, and a share URL contains encoded editor state.
                    </Typography>

                    <SubTitle>What is the difference between a JSON diff and a JSON Patch?</SubTitle>
                    <Typography sx={{ color: 'var(--fg-muted)', mb: 3 }}>
                        A JSON diff shows what changed in a human-readable form. A JSON Patch (RFC 6902) is a machine-readable list of operations that transforms one document into the other. Here you get both: the same list works as a readable diff and as a patch you can apply with the{' '}
                        <Link href="/" style={{ color: 'var(--accent)' }}>JSON Patch Generator</Link> or any RFC 6902 library.
                    </Typography>

                    <SubTitle>Does key order matter when comparing JSON?</SubTitle>
                    <Typography sx={{ color: 'var(--fg-muted)', mb: 3 }}>
                        No. Unlike text-based diff tools that compare line by line, this comparison is structural: reordered keys are not reported as changes. Only real value, type, and structure differences appear.
                    </Typography>
                </Box>
            </Box>

            <Box sx={{ mt: 8, textAlign: 'center' }}>
                <SectionTitle>Go further with JSON Patch</SectionTitle>
                <Typography sx={{ color: 'var(--fg-muted)', mb: 3, maxWidth: '600px', mx: 'auto' }}>
                    Learn the RFC 6902 format behind this diff, or follow a hands-on tutorial in your language.
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
                    <Link href="/docs" style={{ textDecoration: 'none' }}>
                        <Box component="span" className="pill-button-primary" sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, px: 3, py: 1.5 }}>
                            Read the docs <ArrowForwardIcon sx={{ fontSize: 18 }} />
                        </Box>
                    </Link>
                    <Link href="/guides" style={{ textDecoration: 'none' }}>
                        <Box component="span" className="pill-button-secondary" sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, px: 3, py: 1.5 }}>
                            Browse the guides <ArrowForwardIcon sx={{ fontSize: 18 }} />
                        </Box>
                    </Link>
                </Box>
            </Box>

            <RelatedPages currentPage="diff" />
        </Editor>
    );
}
